import { registerSearchClientStorageAdapterFactory } from '@atlassian/search-client/persist-refresh/register';
import type { PersistedStorageAdapter } from '@atlassian/search-client/persist-refresh/storage-adapter';

import type { StorageManager } from './StorageManager.ts';

/**
 * Registers the Confluence storage adapter factory for search-client's persisted cache.
 *
 * This enables the combined search config cache to:
 * 1. Use Confluence's StorageManager (which participates in SSR persistence)
 * 2. Have its data included in getRemoteLocalStorage during SSR
 *
 * The adapter factory pattern allows for late-binding, which is important for
 * SSR request scoping - each SSR request can resolve to the correct storage instance.
 *
 * @param getStorageInstance - A function that returns the current storage manager instance.
 *                             Using a getter avoids circular dependencies and allows for
 *                             late binding of the storage instance.
 */
export function registerSearchClientStorageAdapterForConfluence(
	getStorageInstance: () => StorageManager | undefined,
): void {
	registerSearchClientStorageAdapterFactory(() => {
		// NOTE: We intentionally DO NOT capture a StorageManager instance here.
		// In SSR, a request-scoped storage instance may change across async boundaries.
		// Each adapter method resolves the current instance at call time (late binding)
		// to avoid using a stale captured instance.

		const adapter: PersistedStorageAdapter = {
			getItem: (key: string): string | null => {
				const sm = getStorageInstance();
				if (!sm) {
					return null;
				}

				const value = sm.getItem(key);
				// StorageManager may return parsed JSON or the original string
				// The adapter needs to return a string | null
				if (value === null || value === undefined) {
					return null;
				}
				// If value is already a string, return as-is
				if (typeof value === 'string') {
					return value;
				}
				// Otherwise, serialize back to string
				return JSON.stringify(value);
			},
			setItem: (key: string, value: string): void => {
				const sm = getStorageInstance();
				if (!sm) {
					return;
				}
				// eslint-disable-next-line check-set-storage-usage/no-unimported-storage-set
				sm.setItem(key, value);
			},
			removeItem: (key: string): void => {
				const sm = getStorageInstance();
				if (!sm) {
					return;
				}
				sm.removeItem(key);
			},
		};

		return adapter;
	});
}
